import axios from 'axios';
import { installDeviceKey } from './deviceKey';

function getCookie(name) {
    let re = new RegExp(name + "=([^;]+)"),
        value = re.exec(document.cookie);
    return (value != null) ? unescape(value[1]) : null;
}

// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-CSRFToken'] = getCookie('csrftoken');
// Device-bound sessions: send the stored key on same-origin calls, and take
// a freshly issued one from any response that carries it. Installed on the
// default instance, so the handful of components that call axios directly
// (Confirm.vue, Unsubscribe.vue) are covered too.
installDeviceKey(axios);

const capiUrl = '/api/';

// Resource factory - creates API methods similar to vue-resource
function createResource(url) {
    return {
        get(params = {}) {
            // Replace {param} placeholders in URL
            let finalUrl = url;
            for (const [key, value] of Object.entries(params)) {
                if (finalUrl.includes(`{${key}}`)) {
                    finalUrl = finalUrl.replace(`{${key}}`, value);
                    delete params[key];
                }
            }
            return axios.get(finalUrl, { params })
                .then(r => ({ body: r.data }))
                .catch(e => Promise.reject({ body: e.response?.data || {}, status: e.response?.status }));
        },
        save(data = {}, params = {}) {
            let finalUrl = url;
            for (const [key, value] of Object.entries(params)) {
                if (finalUrl.includes(`{${key}}`)) {
                    finalUrl = finalUrl.replace(`{${key}}`, value);
                }
            }
            return axios.post(finalUrl, data)
                .then(r => ({ body: r.data }))
                .catch(e => Promise.reject({ body: e.response?.data || {}, status: e.response?.status }));
        },
        update(data = {}, params = {}) {
            let finalUrl = url;
            for (const [key, value] of Object.entries(params)) {
                if (finalUrl.includes(`{${key}}`)) {
                    finalUrl = finalUrl.replace(`{${key}}`, value);
                }
            }
            return axios.put(finalUrl, data)
                .then(r => ({ body: r.data }))
                .catch(e => Promise.reject({ body: e.response?.data || {}, status: e.response?.status }));
        },
        delete(params = {}) {
            let finalUrl = url;
            for (const [key, value] of Object.entries(params)) {
                if (finalUrl.includes(`{${key}}`)) {
                    finalUrl = finalUrl.replace(`{${key}}`, value);
                    delete params[key];
                }
            }
            return axios.delete(finalUrl, { params })
                .then(r => ({ body: r.data }))
                .catch(e => Promise.reject({ body: e.response?.data || {}, status: e.response?.status }));
        }
    };
}

function cached(resource) {
    let originalGet = resource.get;
    resource._cache = null;
    resource.get = function(params) {
        if (!resource._cache) {
            resource._cache = originalGet(params);
        }
        return resource._cache;
    };
    return resource;
}

// Export services as functions that return resources
export const services = {
    Login: () => createResource(capiUrl + 'users/login'),
    Register: () => createResource(capiUrl + 'users/register'),
    Context: () => createResource(capiUrl + 'users/context'),
    Logout: () => createResource(capiUrl + 'users/logout'),
    Forgot: () => createResource(capiUrl + 'users/forgot'),
    Reset: () => createResource(capiUrl + 'users/reset'),
    Password: () => createResource(capiUrl + 'users/password'),
    Tfa: () => createResource(capiUrl + 'users/tfa'),
    TfaSetup: () => createResource(capiUrl + 'users/tfa/setup'),
    TfaEnable: () => createResource(capiUrl + 'users/tfa/enable'),
    TfaDisable: () => createResource(capiUrl + 'users/tfa/disable'),
    TfaRecoveryCodes: () => createResource(capiUrl + 'users/tfa/recovery-codes'),
    TfaVerify: () => createResource(capiUrl + 'users/tfa/verify'),
    Plan: () => createResource(capiUrl + 'users/plan'),
    PlanLowest: () => cached(createResource(capiUrl + 'users/plan/lowest')),
    CurrencyRate: () => cached(createResource(capiUrl + 'users/currency/RUB')),
    Data: () => cached(createResource(capiUrl + 'dash/data')),
    Stats: () => createResource(capiUrl + 'dash/stats'),
    Token: () => createResource(capiUrl + 'users/token'),
    PromoCode: () => createResource(capiUrl + 'users/promocode'),
    APIKey: () => createResource(capiUrl + 'users/apikey'),
    Transaction: () => createResource(capiUrl + 'users/transaction'),
    Payment: () => createResource(capiUrl + 'users/payment'),
    PaymentSystem: () => createResource(capiUrl + 'users/payment/system'),
    Invoice: () => createResource(capiUrl + 'users/invoice/{id}'),
    Review: () => createResource(capiUrl + 'users/review'),
    ReviewAggregate: () => cached(createResource(capiUrl + 'users/review/aggregate')),
    Payout: () => createResource(capiUrl + 'users/payout'),
    TgLogin: () => createResource(capiUrl + 'users/tg-login'),
    TgComplete: () => createResource(capiUrl + 'users/tg-complete'),
    BlogPosts: () => createResource(capiUrl + 'blog/posts'),
    BlogPost: () => createResource(capiUrl + 'blog/posts/{slug}'),
    Releases: () => createResource(capiUrl + 'releases/'),
    Status: () => cached(createResource(capiUrl + 'status')),
    SiteContacts: () => cached(createResource(capiUrl + 'site-contacts')),
    SiteFooter: () => cached(createResource(capiUrl + 'site-footer')),
    HealthCheck: (proto, domain) => createResource(`${proto}://api.${domain}/sys/healthcheck`),
    Dataset: () => createResource(capiUrl + 'datasets'),
    Products: () => createResource(capiUrl + 'products/'),
    ProductBuy: () => createResource(capiUrl + 'products/buy'),
    Purchases: () => createResource(capiUrl + 'products/purchases/'),
    // One-time nonce that lets a top-level navigation into /oauth/authorize
    // count as authenticated — see utils/redirect.js::navigateToNext.
    OAuthNav: () => createResource('/oauth/nav'),
    OAuthGrants: () => createResource('/oauth/grants'),
    OAuthGrant: () => createResource('/oauth/grants/{grant_id}'),
};

export default services;
